import React, { useEffect, useState } from 'react';
import StorefrontAPI from 'utilities/storefrontApi';
import { loadSession } from 'utilities/storefrontSession';
import { CARD, SUB_INPUT, BUTTON, brandPath } from './styles';
import Stars from './Stars';

/**
 * Product reviews panel: list of approved reviews on the left, a
 * submission form on the right (only visible to signed-in shoppers).
 *
 * Submissions land as `pending` server-side — they appear after the
 * brand owner approves them in the dashboard.
 */
export default function ProductReviews({ brand, product, accent }) {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [signedIn, setSignedIn] = useState(false);
  const [form, setForm] = useState({ rating: 5, title: '', body: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitMsg, setSubmitMsg] = useState('');
  const [submitErr, setSubmitErr] = useState('');

  useEffect(() => {
    if (!brand?._id || !product?._id) return;
    setLoading(true);
    StorefrontAPI.listProductReviews(brand._id, product._id)
      .then((res) => setReviews(Array.isArray(res) ? res : []))
      .catch(() => setReviews([]))
      .finally(() => setLoading(false));
    setSignedIn(Boolean(loadSession(brand._id)?.token));
  }, [brand?._id, product?._id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!brand?._id || !product?._id) return;
    setSubmitErr(''); setSubmitMsg('');
    setSubmitting(true);
    try {
      await StorefrontAPI.createProductReview(brand._id, {
        product_id: product._id,
        rating: form.rating,
        title: form.title,
        body: form.body,
      });
      setSubmitMsg('Thanks — your review is pending approval.');
      setForm({ rating: 5, title: '', body: '' });
    } catch (err) {
      setSubmitErr(err?.message || 'Could not submit');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ marginTop: 32 }}>
      <h2 style={{ fontSize: 20, margin: '0 0 14px' }}>Reviews</h2>
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr)', gap: 24 }}>
        <div style={CARD}>
          {Number(product.review_count || 0) > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
              <Stars value={product.rating_avg} />
              <div style={{ fontSize: 14, color: '#475569' }}>
                {Number(product.rating_avg || 0).toFixed(2)} · {product.review_count} review{product.review_count === 1 ? '' : 's'}
              </div>
            </div>
          )}
          {loading && <div style={{ color: '#64748b' }}>Loading…</div>}
          {!loading && reviews.length === 0 && (
            <div style={{ color: '#64748b', fontSize: 14 }}>No reviews yet — be the first.</div>
          )}
          {reviews.map((r) => (
            <div key={r._id} style={{ borderTop: '1px solid #e5e7eb', paddingTop: 12, marginTop: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <Stars value={r.rating} size={14} />
                {r.verified_purchase && (
                  <span style={{ fontSize: 11, color: '#16a34a', fontWeight: 700, textTransform: 'uppercase' }}>
                    Verified buyer
                  </span>
                )}
              </div>
              {r.title && <div style={{ fontWeight: 700, marginBottom: 4 }}>{r.title}</div>}
              {r.body && <div style={{ color: '#475569', whiteSpace: 'pre-wrap', fontSize: 14 }}>{r.body}</div>}
              <div style={{ color: '#94a3b8', fontSize: 12, marginTop: 6 }}>
                {r.author_name} · {r.createdAt ? new Date(r.createdAt).toLocaleDateString() : ''}
              </div>
            </div>
          ))}
        </div>

        <div style={CARD}>
          <div style={{ fontWeight: 700, fontSize: 14, color: '#475569', marginBottom: 10 }}>
            Write a review
          </div>
          {!signedIn ? (
            <div style={{ color: '#64748b', fontSize: 13 }}>
              <a href={brandPath(brand, '/login')} style={{ color: accent, fontWeight: 600 }}>Sign in</a>
              {' '}to leave a review.
            </div>
          ) : (
            <form onSubmit={submit}>
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 12, color: '#475569', marginBottom: 4 }}>Your rating</div>
                <Stars value={form.rating} onChange={(n) => setForm((f) => ({ ...f, rating: n }))} size={22} />
              </div>
              <input
                placeholder='Title (optional)'
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                style={{ ...SUB_INPUT, marginBottom: 8 }}
              />
              <textarea
                placeholder='Share your experience…'
                value={form.body}
                onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
                style={{ ...SUB_INPUT, minHeight: 90, marginBottom: 10, resize: 'vertical' }}
              />
              <button
                type='submit'
                disabled={submitting}
                style={{ ...BUTTON(accent), width: '100%' }}
              >
                {submitting ? 'Submitting…' : 'Submit'}
              </button>
              {submitMsg && <div style={{ color: '#16a34a', fontSize: 13, marginTop: 8 }}>{submitMsg}</div>}
              {submitErr && <div style={{ color: '#dc2626', fontSize: 13, marginTop: 8 }}>{submitErr}</div>}
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
